import { Injectable, InjectionToken, inject } from '@angular/core';
import { OktaAuth, SignoutOptions } from '@okta/okta-auth-js';
import { AuthService, OktaAccessTokenContext, WindowService } from '@drc-eca/eca-components-lib';
import { environment } from '../../../environments/environment';
export const OKTA_AUTH_CLIENT = new InjectionToken<any>('OKTA_AUTH_CLIENT');

const PASSWORD_SCOPES = ['openid', 'profile', 'okta.myAccount.password.read', 'okta.myAccount.password.manage'];
const REQUIRED_PASSWORD_SCOPES = ['okta.myAccount.password.read', 'okta.myAccount.password.manage'];

@Injectable({
  providedIn: 'root'
})
export class OktaService {
  private oktaAuthClient = inject<OktaAuth>(OKTA_AUTH_CLIENT);
  private authService = inject(AuthService);
  private windowService = inject(WindowService);

  isUserLoggedIn(): Promise<boolean> {
    return this.oktaAuthClient.session.exists();
  }

  signIn(credentials: any) {
    return this.oktaAuthClient.signIn(credentials);
  }

  setCookieAndRedirect(sessionToken: string, redirectUrl?: string) {
    if (redirectUrl) {
      return this.oktaAuthClient.session.setCookieAndRedirect(sessionToken, redirectUrl);
    }
    return this.oktaAuthClient.session.setCookieAndRedirect(sessionToken);
  }

  sessionExists() {
    return this.oktaAuthClient.session.exists();
  }

  getTokens(scopes: string[]) {
    return this.oktaAuthClient.token.getWithoutPrompt({ scopes });
  }

  private isPasswordAccessToken(context: OktaAccessTokenContext | undefined, tenantOrigin: string): boolean {
    return context?.tenantOrigin === tenantOrigin &&
      REQUIRED_PASSWORD_SCOPES.every(scope => context.scopes.includes(scope));
  }

  private async getPasswordAccessToken(tenantOrigin: string): Promise<string | undefined> {
    try {
      const context = await this.authService.getOktaAccessTokenContext(PASSWORD_SCOPES);
      return this.isPasswordAccessToken(context, tenantOrigin) ? context?.accessToken : undefined;
    } catch {
      // The caller retains its existing session-cookie and service fallback behavior.
      return undefined;
    }
  }

  private async getPasswordHeaders(tenantOrigin: string, includeContentType: boolean): Promise<Record<string, string>> {
    const headers: Record<string, string> = { Accept: 'application/json; okta-version=1.0.0' };
    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }
    const accessToken = await this.getPasswordAccessToken(tenantOrigin);
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    return headers;
  }

  async changePassword(tenantOrigin: string, currentPassword: string, newPassword: string): Promise<any> {
    const headers = await this.getPasswordHeaders(tenantOrigin, true);

    const response = await fetch(`${tenantOrigin}/idp/myaccount/password/change-password`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ oldPassword: currentPassword, newPassword })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw { status: response.status, ...payload };
    }
    return payload;
  }

  async getPasswordComplexityRequirements(tenantOrigin: string): Promise<any> {
    const headers = await this.getPasswordHeaders(tenantOrigin, false);

    const response = await fetch(`${tenantOrigin}/idp/myaccount/password/complexity-requirements`, {
      credentials: 'include',
      headers
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw { status: response.status, ...payload };
    }
    return payload;
  }

  /**
   * Tells Okta to logout
   * @param postLogoutRedirectUri URI from the Okta dashboard
   * @param state The state to return to. If any URL be sure to have it encoded before sending. The state is returned as a csv (state=csv)
   */
  async logout(postLogoutRedirectUri?: string, state: string[] = []): Promise<void> {
    if (environment.useNewLoginPage) {
      try {
        await this.oktaAuthClient.stop();
      } catch (e) {
        console.error('stop: ', e);
      }
      try {
        const opts = {
          revokeAccessToken: true,
          revokeRefreshToken: true,
          clearTokensBeforeRedirect: true,
        } as SignoutOptions;
        if (postLogoutRedirectUri) {
          opts.postLogoutRedirectUri = postLogoutRedirectUri;
        }
        if (state.length) {
          opts.state = state.join(',');
        }
        await this.oktaAuthClient.signOut(opts);
      } catch (e) {
        console.error('signOut: ', e);
      }
    } else {
      if (await this.isUserLoggedIn()) {
        await this.oktaAuthClient.revokeAccessToken();
        await this.oktaAuthClient.closeSession();
      }
    }
  }

  async signOutOfTenant(orgOrigin: string, fromUri: string, state: string[] = []): Promise<void> {
    const fromUriUrl = new URL(fromUri);
    if (state.length) {
      fromUriUrl.searchParams.set('state', state.join(','));
    }
    const signOutUrl = new URL('/login/signout', orgOrigin);
    signOutUrl.searchParams.set('fromURI', fromUriUrl.toString());
    this.windowService.navigateTo(signOutUrl.toString());
  }

  async closeTenantSession(tenantOrigin: string): Promise<boolean> {
    const tenantAuthClient = new OktaAuth({
      ...environment.okta.clientConfig,
      issuer: new URL('/oauth2/default', tenantOrigin).toString()
    });
    return tenantAuthClient.closeSession();
  }

  async closeSession(): Promise<void> {
    try {
      await this.oktaAuthClient.closeSession();
    } catch {
      // Session may already be closed or expired
    }
  }
}
